// src/pages/Login.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api/api";

export default function Login() {
  const navigate = useNavigate();
  const { setUser, setIsLoggedIn, applyTheme } = useAuth();
  const [form, setForm] = useState({ identifier: "", password: "" });
  const [error, setError] = useState(null);
  const [unverified, setUnverified] = useState(false);
  const [resendStatus, setResendStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    setUnverified(false);
    setResendStatus(null);
    try {
      const res = await api.post("/auth/login", {
        identifier: form.identifier.trim(),
        password: form.password,
      });

      // ✅ Populate user context immediately
      if (res.data.user) {
        setUser(res.data.user);
      } else {
        // fallback: validate cookies
        const me = await api.get("/auth/me");
        setUser(me.data);
      }

      // Apply theme before navigating so the protected page renders in the right mode
      try {
        const sr = await api.get("/settings/");
        applyTheme(sr.data.dark_mode);
      } catch (_) {}

      setIsLoggedIn(true);
      setForm({ identifier: "", password: "" });
      navigate("/"); // redirect home
    } catch (err) {
      console.error("Login failed:", err);
      if (err.response?.status === 403 && err.response?.data?.detail?.includes("not verified")) {
        setUnverified(true);
        setError(null);
      } else {
        setError(err.response?.data?.detail || "Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const [showDemoPrompt, setShowDemoPrompt] = useState(false);

  const handleDemoLogin = async () => {
    setShowDemoPrompt(false);
    setLoading(true);
    setError(null);
    sessionStorage.removeItem("demo-welcomed");
    sessionStorage.removeItem("demo-panel-dismissed");
    try {
      const res = await api.post("/auth/login", {
        identifier: "demo",
        password: "D3m0CardStoard!",
      });
      if (res.data.user) {
        setUser(res.data.user);
      } else {
        const me = await api.get("/auth/me");
        setUser(me.data);
      }
      try {
        const sr = await api.get("/settings/");
        applyTheme(sr.data.dark_mode);
      } catch (_) {}
      setIsLoggedIn(true);
      navigate("/");
    } catch (err) {
      setError("Demo login unavailable. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendStatus("sending");
    try {
      await api.post("/auth/resend-verify", { identifier: form.identifier.trim() });
      setResendStatus("sent");
    } catch {
      setResendStatus("error");
    }
  };

  return (
    <div className="login-container" style={{ margin: "2rem auto", textAlign: "center" }}>
      {/* Logo */}
      <div className="logo-slot" style={{ marginBottom: "1rem" }}>
        <img
          src="/logo.png"
          alt="CardStoard Logo"
          style={{ width: "550px", height: "auto" }}
        />
      </div>

      <h2>Login</h2>

      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          alignItems: "center",
          maxWidth: "320px",
          margin: "0 auto",
        }}
      >
        <input
          type="text"
          name="identifier"
          placeholder="Email or Username"
          value={form.identifier}
          onChange={handleChange}
          required
          style={{ width: "100%", padding: "0.5rem" }}
        />
        <input
          type="password"
          name="password"
          placeholder="Password"
          value={form.password}
          onChange={handleChange}
          required
          style={{ width: "100%", padding: "0.5rem" }}
        />
        <button
          type="submit"
          className="nav-btn"
          style={{ padding: "0.75rem", fontSize: "1rem", width: "100%" }}
          disabled={loading}
        >
          {loading ? "Signing in..." : "Log In"}
        </button>

        {error && <p style={{ color: "red", marginTop: "0.5rem" }}>{error}</p>}
        {unverified && (
          <div style={{ marginTop: "0.75rem", padding: "0.75rem 1rem", background: "#fff8e1", border: "1px solid #f59e0b", borderRadius: 8, fontSize: "0.9rem", color: "#92400e" }}>
            <p style={{ margin: "0 0 0.5rem" }}>Your email hasn't been verified yet. Check your inbox and spam folder for the verification link.</p>
            {resendStatus === "sent"
              ? <p style={{ margin: 0, color: "#166534" }}>✅ Verification email resent — check your inbox and spam folder.</p>
              : resendStatus === "error"
              ? <p style={{ margin: 0, color: "red" }}>Failed to resend. Please try again.</p>
              : <button onClick={handleResend} disabled={resendStatus === "sending"}
                  style={{ background: "none", border: "none", color: "#92400e", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: "0.9rem" }}>
                  {resendStatus === "sending" ? "Sending…" : "Resend verification email"}
                </button>
            }
          </div>
        )}
      </form>

      <p style={{ marginTop: "1rem" }}>
        Don’t have an account? <Link to="/register">Register</Link>
        <span style={{ margin: "0 0.5rem", color: "#ccc" }}>|</span>
        <Link to="/forgot-password">Forgot password?</Link>
      </p>

      <div style={{ marginTop: "1.5rem", borderTop: "1px solid var(--border)", paddingTop: "1.25rem" }}>
        <p style={{ margin: "0 0 0.75rem", fontSize: "0.9rem", color: "var(--text-muted)" }}>
          Want to explore first?
        </p>
        <button
          className="nav-btn"
          onClick={() => setShowDemoPrompt(true)}
          disabled={loading}
          style={{ background: "#1a7a1a" }}
        >
          Try a Demo
        </button>
        <p style={{ margin: "0.5rem 0 0", fontSize: "0.78rem", color: "var(--text-muted)" }}>
          Read-only demo account — no signup required
        </p>

        {showDemoPrompt && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{
              background: "var(--bg-card, #fff)", borderRadius: "12px", padding: "2rem",
              maxWidth: "380px", width: "90%", textAlign: "center",
              boxShadow: "0 8px 32px rgba(0,0,0,0.25)", border: "2px solid #f59e0b",
            }}>
              <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>🔒</div>
              <h3 style={{ margin: "0 0 0.75rem" }}>Demo Mode — Read Only</h3>
              <p style={{ margin: "0 0 1.5rem", color: "#555", fontSize: "0.95rem", lineHeight: 1.5 }}>
                This is a <strong>read-only</strong> experience — changes to data and settings are not permitted.
                Sign up for free to start tracking your own collection.
              </p>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center" }}>
                <button className="nav-btn" style={{ background: "#1a7a1a" }} onClick={handleDemoLogin}>
                  Enter Demo
                </button>
                <button className="nav-btn" onClick={() => { setShowDemoPrompt(false); navigate("/register"); }}>
                  Sign Up Free
                </button>
              </div>
              <button onClick={() => setShowDemoPrompt(false)}
                style={{ marginTop: "1rem", background: "none", border: "none", color: "#aaa", cursor: "pointer", fontSize: "0.85rem" }}>
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
